import React, { useId, useState } from "react";
import { DatasetBarChartProps, IDataSet } from "components/dashboard/charts/interfaces";
import ChartWrapper from "components/dashboard/charts/chart-wrapper/ChartWrapper";
import { T } from "services/lang/LanguageService";
import DatasetBarChart from "components/dashboard/charts/bar-chart/DatasetBarChart";
import { APIResponseToDatasetDataInfoResponseArray } from "components/dashboard/charts/bar-chart/DatasetBarChartDataConverter";
import Plotly, { ModeBarButton, ModeBarDefaultButtons } from "plotly.js";
import { APIResponse } from "services/HttpService";
import useTimeState from "hooks/dashboard/useTimeState";
import { clipboardIcon, questionMarkIcon } from "../icons";
import copyChartToClipBoard from "../chartUtils";
import DatasetsTitlesShower from "../DatasetsTitlesShower";


const DatasetBarChartRenderer = (props: DatasetBarChartProps) => {
  const [titlesDialog, setTitlesDialog] = useState(false);
  const uniqueId = useId();
  const overriddenTitles = (props.datasets || []).filter(dataset => dataset.overriddenTitle);

  const timeState = useTimeState({
    from: props.from,
    to: props.to,
  });

  const titlesAndOverriddenTitles =
    overriddenTitles.length > 0
      ? (props.datasets || []).map(dataset => ({
          title: dataset.identifier,
          overriddenTitle: dataset.overriddenTitle!,
        }))
      : [];

  const chartConfig: Partial<Plotly.Config> = {
    modeBarButtonsToAdd: [
      {
        title: T("chart-config.copy-chart-to-clipboard"),
        name: "Copy chart to Clipboard",
        icon: clipboardIcon,
        click: copyChartToClipBoard,
      },
      overriddenTitles.length > 0
        ? {
          title: T("chart-config.show-titles"),
          name: "Show Titles",
            icon: questionMarkIcon,
            click: () => setTitlesDialog(true),
          }
        : null,
    ].filter(Boolean) as ModeBarDefaultButtons[] | ModeBarButton[] | undefined,
  };
  
  const identifiers = props.datasets?.map(dataset => dataset.identifier);

  const promiseHandler = (res: APIResponse<IDataSet>) => {
    const timeFormat = props.xAxisType === "category" ? props.dateFormat : undefined;
    return APIResponseToDatasetDataInfoResponseArray(res, props.datasets || [], timeFormat);
  };

  return (
    <ChartWrapper
      uniqueKey={uniqueId}
      identifiers={identifiers || props.identifiers}
      title={props.title}
      showDateSelection
      promiseHandler={promiseHandler}
      updateFrequency={props.updateFrequency}
      loadConf={props.loadConf}
      dateFrom={timeState.from}
      dateTo={timeState.to}
      hideRefresh={props.hideRefresh}
      dynamicHeight={props.dynamicHeight}
      referenceTime={props.referenceTime}
      datasetChart="bar"
      chartConfig={chartConfig}
      width={props.width}
      reportMode={props.reportMode}
      hideHeader={props.hideHeader}
    >
      {(wrapper: any) => (
        <div>
          {titlesDialog && (
            <DatasetsTitlesShower
              dialog={titlesDialog}
              setDialog={setTitlesDialog}
              titlesAndOverriddenTitles={titlesAndOverriddenTitles}
            />
          )}
          <DatasetBarChart
            data={wrapper.data}
            width={wrapper.width}
            height={wrapper.height}
            dateFormat={props.xAxisType === "date" ? props.dateFormat : undefined}
            xaxisType={props.xAxisType}
            chartConfig={wrapper.chartConfig}
          />
        </div>
      )}
    </ChartWrapper>
  );
};

export default DatasetBarChartRenderer;
